import { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Wrench, Settings, Cog } from 'lucide-react';
import { cn } from '@clarix/ui';
import { useNetworkStatus } from '@clarix/hooks';
import { fetchSystemStatus } from '@clarix/api-client';
import { env } from '@/config/env';

/* ------------------------------------------------------------------ */
/*  Floating particle — small tool icons drifting upward               */
/* ------------------------------------------------------------------ */
interface FloatingParticleProps {
  delay: number;
  duration: number;
  left: string;
  size: number;
  icon: 'wrench' | 'cog' | 'settings';
}

function FloatingParticle({ delay, duration, left, size, icon }: FloatingParticleProps) {
  const Icon = icon === 'wrench' ? Wrench : icon === 'cog' ? Cog : Settings;
  return (
    <div
      className="maintenance-float-up absolute bottom-0 opacity-0"
      style={
        {
          left,
          '--delay': `${delay}s`,
          '--dur': `${duration}s`,
          width: size,
          height: size,
        } as React.CSSProperties
      }
    >
      <Icon className="text-indigo-400/60 h-full w-full" strokeWidth={1.5} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Gear SVG — background decoration                                   */
/* ------------------------------------------------------------------ */
interface GearProps {
  size: number;
  className?: string;
  teeth?: number;
}

function GearSvg({ size, className, teeth = 8 }: GearProps) {
  const outerR = size / 2;
  const innerR = outerR * 0.65;
  const holeR = outerR * 0.25;
  const toothDepth = outerR * 0.18;
  const cx = outerR;
  const cy = outerR;

  const pathParts: string[] = [];
  for (let i = 0; i < teeth; i++) {
    const angle = (i / teeth) * Math.PI * 2;
    const nextAngle = ((i + 0.5) / teeth) * Math.PI * 2;
    const midAngle = ((i + 0.25) / teeth) * Math.PI * 2;
    const midAngle2 = ((i + 0.75) / teeth) * Math.PI * 2;
    const fullNext = ((i + 1) / teeth) * Math.PI * 2;

    const r1 = innerR;
    const r2 = outerR + toothDepth;

    pathParts.push(
      `${i === 0 ? 'M' : 'L'} ${cx + r1 * Math.cos(angle)} ${cy + r1 * Math.sin(angle)}`,
      `L ${cx + r2 * Math.cos(midAngle)} ${cy + r2 * Math.sin(midAngle)}`,
      `L ${cx + r2 * Math.cos(nextAngle)} ${cy + r2 * Math.sin(nextAngle)}`,
      `L ${cx + r1 * Math.cos(midAngle2)} ${cy + r1 * Math.sin(midAngle2)}`,
      `L ${cx + r1 * Math.cos(fullNext)} ${cy + r1 * Math.sin(fullNext)}`,
    );
  }

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className={className} fill="none">
      <path d={`${pathParts.join(' ')} Z`} fill="currentColor" fillRule="evenodd" />
      <circle cx={cx} cy={cy} r={holeR} fill="white" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Maintenance page                                                   */
/* ------------------------------------------------------------------ */
interface MaintenancePageProps {
  /** Operator-supplied message from GET /system/status. Overrides the default copy. */
  message?: string;
  /** Operator-supplied ETA string (e.g. "10:30 PM IST"). */
  eta?: string;
  /**
   * Caller-owned retry. When provided (e.g. AppRoute's per-app outage), the
   * "Try Again" button calls this instead of polling+reloading, and the global
   * self-poll is disabled — the parent owns recovery (a remount). When omitted
   * (AppStatusGate's boot gate), the page self-polls GET /system/status and
   * reloads the moment the platform recovers.
   */
  onRetry?: () => void;
}

const PARTICLES: FloatingParticleProps[] = [
  { delay: 0, duration: 8, left: '10%', size: 16, icon: 'wrench' },
  { delay: 2, duration: 10, left: '25%', size: 12, icon: 'cog' },
  { delay: 4, duration: 9, left: '45%', size: 14, icon: 'settings' },
  { delay: 1, duration: 11, left: '65%', size: 10, icon: 'wrench' },
  { delay: 3, duration: 8, left: '80%', size: 16, icon: 'cog' },
  { delay: 5, duration: 10, left: '90%', size: 12, icon: 'settings' },
  { delay: 6, duration: 9, left: '35%', size: 11, icon: 'wrench' },
  { delay: 7, duration: 12, left: '55%', size: 13, icon: 'cog' },
];

/**
 * Shell-owned, FULL-SCREEN maintenance page (design ported from clario-frontend).
 *
 * Rendered by AppStatusGate when the platform reports `{ maintenance: true }` or
 * the backend is unreachable while online. It self-polls `GET /system/status`
 * every 5s and reloads the moment the backend recovers, so users never have to
 * refresh manually. When the browser goes offline it steps aside (blank) so the
 * global OfflineBanner strip owns the connectivity message.
 */
export function MaintenancePage({ message, eta, onRetry }: MaintenancePageProps = {}) {
  const [isRetrying, setIsRetrying] = useState(false);
  const [liveMessage, setLiveMessage] = useState<string | undefined>(message);
  const [liveEta, setLiveEta] = useState<string | undefined>(eta);
  const { isOnline } = useNetworkStatus();

  const checkBackendHealth = useCallback(async (): Promise<boolean> => {
    try {
      const status = await fetchSystemStatus(env.VITE_API_BASE_URL);
      if (!status.maintenance) {
        // Recovered — re-run the whole boot gate.
        window.location.reload();
        return true;
      }
      // Still down — refresh copy in case the operator updated it.
      setLiveMessage(status.message);
      setLiveEta(status.eta);
    } catch {
      // Backend still unreachable — expected during the outage window.
    }
    return false;
  }, []);

  const handleRetry = async () => {
    // Caller-owned retry (per-app outage) → hand recovery back to the parent
    // (a remount) instead of polling the global status + reloading.
    if (onRetry) {
      onRetry();
      return;
    }
    setIsRetrying(true);
    const healthy = await checkBackendHealth();
    if (!healthy) setIsRetrying(false);
  };

  useEffect(() => {
    // Parent-owned retry → no global self-poll (avoids reloading on a healthy
    // platform while a single app's backend is still down).
    if (onRetry) return undefined;
    if (!isOnline) return undefined;

    let mounted = true;
    let intervalId: number | undefined;

    // Polls an external system (the backend) and updates state from the async
    // result — the legitimate "subscribe + setState in a callback" effect. The
    // lint rule can't see through the useCallback to the post-await setState.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void checkBackendHealth().then((healthy) => {
      if (healthy || !mounted) return;
      intervalId = window.setInterval(() => {
        void checkBackendHealth().then((ok) => {
          if (ok && mounted && intervalId !== undefined) window.clearInterval(intervalId);
        });
      }, 5000);
    });

    return () => {
      mounted = false;
      if (intervalId !== undefined) window.clearInterval(intervalId);
    };
  }, [checkBackendHealth, isOnline, onRetry]);

  // Offline → let the global OfflineBanner (z-[9999]) own the message.
  if (!isOnline) return <div className="fixed inset-0 z-[9998] bg-white" />;

  return (
    // Full-viewport overlay: covers the shell chrome (TopBar, rail, pinned
    // sidebar) so maintenance reads as a whole-page state, not a panel wedged
    // into the content area. Sits just below the OfflineBanner (z-[9999]).
    <div className="fixed inset-0 z-[9998] flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-50 via-indigo-50/50 to-purple-50 px-4">
      {/* Animated gradient overlay */}
      <div className="maintenance-gradient-shift pointer-events-none absolute inset-0 bg-gradient-to-tr from-indigo-100/30 via-transparent to-purple-100/30 bg-[length:200%_200%]" />

      {/* Floating particles */}
      <div className="pointer-events-none absolute inset-0">
        {PARTICLES.map((p) => (
          <FloatingParticle key={`particle-${p.left}-${p.delay}`} {...p} />
        ))}
      </div>

      {/* Rotating gears */}
      <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        <div className="maintenance-gear-slow absolute -left-32 -top-28">
          <GearSvg size={120} className="text-indigo-100" teeth={10} />
        </div>
        <div className="maintenance-gear-medium-reverse absolute -right-24 -top-12">
          <GearSvg size={80} className="text-purple-100" teeth={8} />
        </div>
        <div className="maintenance-gear-fast absolute -bottom-20 -left-16">
          <GearSvg size={60} className="text-indigo-100/70" teeth={6} />
        </div>
      </div>

      {/* Content card */}
      <div className="relative z-10 w-full max-w-md text-center">
        {/* Animated icon with pulse rings */}
        <div className="relative mx-auto mb-8 h-28 w-28">
          <div className="maintenance-pulse-ring absolute inset-0 rounded-full border-2 border-indigo-300/50" />
          <div className="maintenance-pulse-ring-delayed-1 absolute inset-0 rounded-full border-2 border-indigo-300/30" />
          <div className="maintenance-pulse-ring-delayed-2 absolute inset-0 rounded-full border-2 border-indigo-200/20" />

          <div className="absolute inset-3 flex items-center justify-center rounded-full bg-white shadow-lg shadow-indigo-100/50 ring-1 ring-indigo-100">
            <div className="relative">
              <Settings className="maintenance-icon-spin h-11 w-11 text-indigo-500" strokeWidth={1.5} />
              <Wrench
                className="maintenance-wiggle absolute -bottom-1 -right-1 h-5 w-5 text-indigo-400"
                strokeWidth={1.5}
              />
            </div>
          </div>
        </div>

        <h1 className="mb-3 text-5xl font-semibold tracking-tight text-gray-900">
          Under Maintenance
        </h1>

        <p className="mb-3 text-xl leading-relaxed text-gray-500">
          {liveMessage ??
            "Our servers are currently being tuned up. We're working to get things back up as quickly as possible."}
        </p>

        {liveEta && (
          <p className="mb-3 text-lg font-medium text-gray-600">Expected recovery: {liveEta}</p>
        )}

        {/* Animated progress dots */}
        <div className="mb-8 flex items-center justify-center gap-1.5">
          <span className="text-lg text-gray-400">Working on it</span>
          <span className="inline-flex gap-0.5">
            <span className="maintenance-bounce-dot h-1 w-1 rounded-full bg-indigo-400" />
            <span className="maintenance-bounce-dot-delayed-1 h-1 w-1 rounded-full bg-indigo-400" />
            <span className="maintenance-bounce-dot-delayed-2 h-1 w-1 rounded-full bg-indigo-400" />
          </span>
        </div>

        <button
          type="button"
          onClick={handleRetry}
          disabled={isRetrying || !isOnline}
          className={cn(
            'inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-6 py-3 text-xl font-medium text-white shadow-md shadow-indigo-200 transition-all',
            'hover:bg-indigo-700 hover:shadow-lg hover:shadow-indigo-200 hover:-translate-y-0.5',
            'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:shadow-md',
          )}
        >
          <RefreshCw className={cn('h-4 w-4', isRetrying && 'animate-spin')} strokeWidth={1.5} />
          {isRetrying ? 'Checking...' : 'Try Again'}
        </button>
      </div>
    </div>
  );
}
